/**
 * Debounced GA4 search analytics for Typesense-powered search (TEC-443).
 * Fires `search` and `search_no_results` events with custom dimensions.
 */

import { sendGA4Event } from './providers/ga4';
import { isClientAnalyticsAllowed } from '../cookie-consent/client-analytics-allowed';

export interface SearchAnalyticsGa4Params {
  search_term: string;
  search_locale: string;
  search_result_count: number;
  search_has_results: boolean;
  search_filters: string;
  search_sort: string;
}

let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let pendingParams: SearchAnalyticsGa4Params | null = null;

const DEBOUNCE_MS = 1000;

function fireSearchAnalyticsEvents(params: SearchAnalyticsGa4Params): void {
  if (!isClientAnalyticsAllowed() || typeof window === 'undefined') return;

  sendGA4Event('search', {
    search_term: params.search_term,
    search_locale: params.search_locale,
    search_result_count: params.search_result_count,
    search_has_results: params.search_has_results,
    search_filters: params.search_filters,
    search_sort: params.search_sort,
  });

  if (!params.search_has_results) {
    sendGA4Event('search_no_results', {
      search_term: params.search_term,
      search_locale: params.search_locale,
      search_filters: params.search_filters,
    });
  }
}

/**
 * Schedule GA4 search analytics with 1s debounce to avoid keystroke noise.
 */
export function trackSearchAnalyticsGa4Debounced(params: SearchAnalyticsGa4Params): void {
  pendingParams = params;
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    if (pendingParams) {
      fireSearchAnalyticsEvents(pendingParams);
      pendingParams = null;
    }
    debounceTimer = null;
  }, DEBOUNCE_MS);
}

/** Immediate flush — useful in tests. */
export function flushSearchAnalyticsGa4ForTests(): void {
  if (debounceTimer) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }
  if (pendingParams) {
    fireSearchAnalyticsEvents(pendingParams);
    pendingParams = null;
  }
}

export function buildSearchFiltersJson(filters: unknown): string {
  try {
    return JSON.stringify(filters ?? {});
  } catch {
    return '{}';
  }
}

export function resolveSearchSortLabel(sort: 'relevance' | 'date' | 'salary'): string {
  if (sort === 'date') return 'postedDate:desc';
  if (sort === 'salary') return 'salaryRangeMin:desc';
  return 'score';
}
